import { type JSXElementConstructor, type ReactElement, useMemo } from 'react'

import EditIcon from '@mui/icons-material/Edit'
import type { DialogLike } from '@/components/ConsoleDialog/handlers/dialogHandlers.types'
import { useUnmountingDelay } from '@/hooks/useUnmountingDelay'
import { warnDev } from '@/utils/warnDev'

import { CheckboxMenuItem } from '../CheckboxMenuItem/CheckboxMenuItem'
import { useCheckboxMenuContext } from '../context/CheckboxMenuContext'
import { CheckboxMenuItemLike } from '../index.types'

type Props<Data extends Record<string, unknown>> = CheckboxMenuItemLike & {
  getForm: (rows: Data[]) => ReactElement<DialogLike, JSXElementConstructor<DialogLike>>
  isDisabled?: ((rows: Data[]) => boolean) | boolean
}

export const CheckboxEditSelectedItems = <Data extends Record<string, unknown>>({
  getForm,
  isDisabled = false
}: Props<Data>) => {
  const { close, open, isMounted, isOpen } = useUnmountingDelay()
  const { rows, closeMenu, deselectAll } = useCheckboxMenuContext<Data>()

  const FormDialog = useMemo(() => {
    return getForm(rows)
  }, [getForm, rows])

  const disabled = useMemo(() => {
    if (typeof isDisabled === 'function') {
      return isDisabled(rows)
    }
    return isDisabled
  }, [isDisabled, rows])

  if (!FormDialog) {
    warnDev('getForm must return a valid React element')
    return null
  }

  return (
    <>
      <CheckboxMenuItem text="Edit" onClick={open} icon={<EditIcon />} disabled={disabled} />
      {isMounted && (
        <FormDialog.type
          key={FormDialog.key}
          {...FormDialog.props}
          open={isOpen}
          onClose={() => {
            FormDialog.props?.onClose?.()
            close()
            closeMenu?.()
            deselectAll?.()
          }}
        />
      )}
    </>
  )
}
